import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { setupSchema } from "@laundrypro/shared";
import { Button, ErrorText, FormSection, Input } from "../components/ui";
import { LaundryMark } from "../components/LaundryMark";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch, setCsrfToken } from "../lib/api";

/**
 * First-run setup: creates the business and its single owner account.
 *
 * Reachable only while the installation is uninitialised — the server closes
 * this endpoint permanently once it succeeds, so the screen redirects away
 * rather than offering an action that would be refused.
 */
export function SetupPage() {
  const { branding, language, loading: brandingLoading, refresh: refreshBranding } = useBranding();
  const { refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    businessNameAr: "",
    businessNameEn: "",
    fullName: "",
    username: "",
    password: "",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const ar = language === "ar";

  if (brandingLoading) return null;
  if (!branding.setupRequired) return <Navigate to="/login" replace />;

  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    // The same schema the Worker validates with, so the form cannot accept
    // something the API will reject.
    const parsed = setupSchema.safeParse(form);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) errors[issue.path.join(".")] ??= issue.message;
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiFetch<{ csrfToken: string }>("/setup", {
        method: "POST",
        body: parsed.data,
      });
      setCsrfToken(result.csrfToken);
      await refreshBranding();
      await refreshAuth();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError(ar ? "تعذّر إكمال الإعداد." : "Setup could not be completed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-ink-950 px-4 py-10">
      <div className="w-full max-w-lg animate-slide-up rounded-2xl border border-white/10 bg-card p-6 shadow-lg sm:p-8">
        <div className="mb-6 text-center">
          <LaundryMark size={44} className="mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-ink-900">
            {ar ? "إعداد المنشأة" : "Set up your business"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {ar
              ? "يُنشأ حساب المالك مرة واحدة فقط، ولا يمكن إنشاء حساب مالك آخر لاحقاً."
              : "The owner account is created once. No second owner account can be created afterwards."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormSection legend={ar ? "المنشأة" : "Business"} divided={false}>
            <Input
              label={ar ? "اسم المنشأة (عربي)" : "Business name (Arabic)"}
              value={form.businessNameAr}
              onChange={update("businessNameAr")}
              error={fieldErrors.businessNameAr}
              required
            />
            <Input
              label={ar ? "اسم المنشأة (إنجليزي)" : "Business name (English)"}
              value={form.businessNameEn}
              onChange={update("businessNameEn")}
              error={fieldErrors.businessNameEn}
            />
          </FormSection>

          <FormSection legend={ar ? "حساب المالك" : "Owner account"}>
            <Input
              label={ar ? "الاسم الكامل" : "Full name"}
              value={form.fullName}
              onChange={update("fullName")}
              error={fieldErrors.fullName}
              required
            />
            <Input
              label={ar ? "اسم المستخدم" : "Username"}
              value={form.username}
              onChange={update("username")}
              error={fieldErrors.username}
              autoComplete="username"
              required
            />
            <Input
              label={ar ? "كلمة المرور" : "Password"}
              type="password"
              value={form.password}
              onChange={update("password")}
              error={fieldErrors.password}
              helperText={
                ar
                  ? "١٠ أحرف على الأقل، وتحتوي على حرف ورقم."
                  : "At least 10 characters, including a letter and a number."
              }
              autoComplete="new-password"
              required
            />
          </FormSection>

          <ErrorText>{error}</ErrorText>

          <Button type="submit" size="lg" className="w-full" loading={submitting}>
            {ar ? "إنشاء المنشأة" : "Create business"}
          </Button>
        </form>
      </div>
    </div>
  );
}
